import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ETIQUETAS_ESTADO_VENCIMIENTO,
  leyendaValorizacion,
  type EstadoVencimiento,
} from '@suminuta/shared';
import MarcoReporte, {
  TD,
  TD_NUM,
  TH,
  TarjetaTotal,
} from '../../components/reportes/MarcoReporte';
import {
  CLASE_INPUT,
  Campo,
  Casilla,
  SelectCategoria,
  SelectProveedor,
} from '../../components/reportes/FiltrosComunes';
import { useLotesReporte } from '../../hooks/useReportes';
import { formatearPrecio } from '../../utils/facturaCalculos';
import { formatearFechaCon } from '../../utils/dateUtils';
import BloqueLectura from '../../components/reportes/BloqueLectura';
import {
  EtiquetasDestacado,
  claseFila,
  useDestacados,
} from '../../components/reportes/destacados';

/**
 * Reporte 3 — Lotes y vencimientos (PROMPT_N°054).
 *
 * El semáforo sale de `estadoVencimiento` en `@suminuta/shared`, el mismo
 * que usa el Excel: un lote no puede estar en rojo en la pantalla y en
 * verde en el archivo.
 */

const TONO_SEMAFORO: Record<EstadoVencimiento, string> = {
  vencido: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  por_vencer:
    'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  proximo:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  ok: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  sin_fecha: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
};

const PRESETS = [7, 15, 30];

export default function ReporteLotes() {
  const [params, setParams] = useSearchParams();

  const poner = (clave: string, valor: string | boolean) => {
    const p = new URLSearchParams(params);
    const v = String(valor);
    if (v === '' || v === 'undefined' || v === 'false') p.delete(clave);
    else p.set(clave, v);
    setParams(p, { replace: true });
  };

  const estado = (params.get('estado') ?? 'activos') as
    | 'activos'
    | 'agotados'
    | 'todos';
  const venceEn = params.get('vence_en') ?? '';

  /**
   * Aplica el filtro de una frase clickeable y baja a la tabla. Los filtros
   * viven en la query string (N°053), así que basta con escribirlos ahí:
   * el reporte se vuelve a consultar solo y el link queda compartible.
   */
  const aplicarFiltroDeFrase = (
    filtro: Record<string, string | number | boolean>
  ) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(filtro)) p.set(k, String(v));
    setParams(p, { replace: true });
    document
      .getElementById('tabla-reporte')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filtros = useMemo(
    () => ({
      estado,
      categoria_id: params.get('categoria_id') ?? undefined,
      proveedor_id: params.get('proveedor_id') ?? undefined,
      vence_en: venceEn || undefined,
      vencidos: params.get('vencidos') === 'true',
    }),
    [estado, venceEn, params]
  );

  const { data, isLoading, error } = useLotesReporte(filtros);
  const filas = data?.filas ?? [];
  const sinFecha = filas.filter((f) => f.semaforo === 'sin_fecha').length;
  const destacados = useDestacados(data?.lectura);

  return (
    <MarcoReporte
      nombre="lotes"
      titulo="Lotes y vencimientos"
      descripcion="Qué lotes quedan, cuándo vencen y cuánta plata hay en riesgo."
      filtros={filtros}
      cargando={isLoading}
      error={error}
      vacio={!isLoading && filas.length === 0}
      mensajeVacio="No hay lotes para los filtros aplicados."
      lectura={
        <BloqueLectura
          lectura={data?.lectura}
          ayuda="lotes"
          cargando={isLoading}
          onFiltro={aplicarFiltroDeFrase}
        />
      }
      controles={
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Campo etiqueta="Estado">
            <select
              className={CLASE_INPUT}
              value={estado}
              onChange={(e) => poner('estado', e.target.value)}
            >
              <option value="activos">Con disponible</option>
              <option value="agotados">Agotados</option>
              <option value="todos">Todos</option>
            </select>
          </Campo>
          <SelectCategoria
            valor={params.get('categoria_id') ?? ''}
            onChange={(v) => poner('categoria_id', v)}
          />
          <SelectProveedor
            valor={params.get('proveedor_id') ?? ''}
            onChange={(v) => poner('proveedor_id', v)}
          />
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Vence en
            </label>
            <div className="flex flex-wrap gap-1 mb-2">
              {PRESETS.map((d) => (
                <button
                  key={d}
                  onClick={() => poner('vence_en', venceEn === String(d) ? '' : String(d))}
                  className={`px-2.5 py-1 rounded text-xs font-medium border ${
                    venceEn === String(d)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  ≤ {d} d
                </button>
              ))}
            </div>
            <Casilla
              etiqueta="Sólo vencidos"
              marcada={filtros.vencidos}
              onChange={(v) => poner('vencidos', v)}
            />
          </div>
        </div>
      }
      aviso={
        data && sinFecha === filas.length && filas.length > 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 dark:bg-gray-800 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-300">
            Los {filas.length} lotes están sin fecha de vencimiento, así que el
            semáforo no aplica. No es un error: los abarrotes se cargan sin
            fecha a propósito.
          </div>
        ) : null
      }
      totales={
        data ? (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <TarjetaTotal etiqueta="Lotes" valor={String(data.total.lotes)} />
            <TarjetaTotal
              etiqueta="Valor disponible"
              valor={`$${formatearPrecio(data.total.valor_disponible)}`}
              detalle={leyendaValorizacion({
                lotes_a_referencia: data.total.lotes_a_referencia,
                ingredientes_sin_costo: 0,
                stock_sin_lote: 0,
              })}
              tono="bueno"
            />
            <TarjetaTotal
              etiqueta="Vence en ≤ 7 días"
              valor={`$${formatearPrecio(data.total.valor_vence_7)}`}
              tono={data.total.valor_vence_7 > 0 ? 'alerta' : 'neutro'}
            />
            <TarjetaTotal
              etiqueta="Vencido"
              valor={`$${formatearPrecio(data.total.valor_vencido)}`}
              detalle={`${data.total.vencidos} lote(s) · ≤30 d: $${formatearPrecio(data.total.valor_vence_30)}`}
              tono={data.total.vencidos > 0 ? 'malo' : 'neutro'}
            />
          </div>
        ) : null
      }
    >
      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
        <thead className="bg-gray-50 dark:bg-gray-900/40">
          <tr>
            <th className={TH}>Ingrediente</th>
            <th className={TH}>Lote</th>
            <th className={TH}>Proveedor</th>
            <th className={TH}>Documento origen</th>
            <th className={TH}>Ingreso</th>
            <th className={TH}>Vencimiento</th>
            <th className={TH}>Semáforo</th>
            <th className={`${TH} text-right`}>Inicial</th>
            <th className={`${TH} text-right`}>Disponible</th>
            <th className={`${TH} text-right`}>Costo unit.</th>
            <th className={`${TH} text-right`}>Valor</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {filas.map((f) => (
            <tr
              key={f.id}
              className={
                claseFila(destacados.get(f.id)) ||
                (f.semaforo === 'vencido'
                  ? 'bg-red-50 dark:bg-red-900/20'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-700/40')
              }
            >
              <td className={TD}>
                <div className="font-medium">
                  {f.ingrediente}
                  <EtiquetasDestacado tipos={destacados.get(f.id)} />
                </div>
                {f.categoria && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {f.categoria}
                  </div>
                )}
              </td>
              <td className={`${TD} font-mono text-xs`}>{f.numero_lote}</td>
              <td className={TD}>{f.proveedor ?? '—'}</td>
              <td className={TD}>{f.origen}</td>
              <td className={TD}>
                {formatearFechaCon(f.fecha_ingreso, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </td>
              <td className={TD}>
                {f.fecha_vencimiento
                  ? formatearFechaCon(f.fecha_vencimiento, {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })
                  : '—'}
              </td>
              <td className={TD}>
                <span
                  className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${TONO_SEMAFORO[f.semaforo]}`}
                >
                  {ETIQUETAS_ESTADO_VENCIMIENTO[f.semaforo]}
                  {f.dias_restantes !== null && f.semaforo !== 'ok' && (
                    <> ({f.dias_restantes} d)</>
                  )}
                </span>
              </td>
              <td className={TD_NUM}>
                {f.cantidad_inicial.toLocaleString('es-CL')} {f.unidad}
              </td>
              <td className={`${TD_NUM} font-medium`}>
                {f.disponible.toLocaleString('es-CL')} {f.unidad}
              </td>
              <td className={TD_NUM}>
                {f.costo_unitario === 0 ? (
                  <span className="text-amber-600 dark:text-amber-400">
                    sin costo
                  </span>
                ) : (
                  <>
                    ${f.costo_unitario.toLocaleString('es-CL', { maximumFractionDigits: 2 })}
                    {f.a_precio_referencia && (
                      <span
                        className="ml-1 text-xs text-amber-600 dark:text-amber-400"
                        title="El lote no trae costo: se valoriza al precio de referencia del catálogo"
                      >
                        ref.
                      </span>
                    )}
                  </>
                )}
              </td>
              <td className={`${TD_NUM} font-medium`}>
                ${formatearPrecio(f.valor_disponible)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </MarcoReporte>
  );
}
